<script setup lang="ts">
import { watch, computed, onMounted, onUnmounted, ref, nextTick, provide } from 'vue'
import { RouterLink, RouterView, useRouter, useRoute } from 'vue-router'
import { Bell, ChevronDown, User, LogOut, LogIn, Download } from 'lucide-vue-next'
import { useUserStore, useAccountStore, useDownloadStore } from '@/stores'
import logoImage from '@/assets/logo.png'
import ProfileModal from '@/components/ProfileModal.vue'
import DownloadManagerModal from '@/components/DownloadManagerModal.vue'
import NotificationDropdown from '@/components/NotificationDropdown.vue'
import { ROLE_BADGE_STYLES, DEFAULT_ROLE_BADGE_STYLE } from '@/constants/consts'
import { isRestoreNavigation, saveScrollPosition, getSavedScrollPosition, restoreScrollPosition } from '@/utils/useListState'
import { hasFocusTarget, MAIN_SCROLL_KEY } from '@/router/navState'

// ─── ref, reactive ─────────────────────────────────────────────────────────
const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const accountStore = useAccountStore()
const downloadStore = useDownloadStore()

const isShowProfileModal = ref<boolean>(false)

// ─── 페이지 이동 시 스크롤 상단 초기화 (focus 자동 스크롤 시 예외 처리) ────────────────
const mainContentRef = ref<HTMLElement | null>(null)
// 홈처럼 뒤로가기로 돌아왔을 때 스크롤 위치를 되살리는 화면이 쓴다
provide(MAIN_SCROLL_KEY, mainContentRef)

function resetScrollToTop(): void {
  nextTick(() => {
    if (mainContentRef.value) {
      mainContentRef.value.scrollTop = 0
    }
    window.scrollTo({ top: 0, left: 0 })
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  })
}

// 본문 스크롤 위치를 주소별로 저장 (뒤로 가기 시 복원용)
const mainScrollKey = (fullPath: string) => `main:${fullPath}`
router.beforeEach((_to, from) => {
  if (mainContentRef.value && from.matched.length > 0) {
    saveScrollPosition(mainScrollKey(from.fullPath), mainContentRef.value.scrollTop)
  }
})

router.afterEach((to) => {
  // focus 작업이 있어 스크롤이 자동으로 이동하는 경우 예외 처리
  if (to.hash || hasFocusTarget() || to.meta?.preserveScroll) {
    return
  }

  // 뒤로 가기·브레드크럼으로 돌아온 경우: 맨 위로 올리지 않고 떠날 때의 위치로 복원
  // (목록이 비동기로 그려지므로 restoreScrollPosition이 높이가 찰 때까지 재시도)
  if (isRestoreNavigation(to.path)) {
    const savedTop = getSavedScrollPosition(mainScrollKey(to.fullPath))
    if (savedTop !== null) {
      nextTick(() => {
        if (mainContentRef.value) restoreScrollPosition(mainContentRef.value, savedTop)
      })
      return
    }
  }

  resetScrollToTop()
})

// ─── computed ──────────────────────────────────────────────────────────────
const activeDownloadCount = computed<number>(() => downloadStore.activeTasks.length)
const isLoginPage = computed<boolean>(() => route.path === '/login')

// 사용자 드롭다운 머리글의 권한 배지: 이름은 내 정보 수정의 "현재 권한"과 같은 라벨, 색은 Projects 헤더 배지와 동일
const userRoleBadge = computed(() => {
  const role = (userStore.currentUser.role || accountStore.roleId || '').toUpperCase()
  const accent = userStore.roleAccentColor as { label?: string }
  return {
    label: accent?.label || role,
    style: ROLE_BADGE_STYLES[role] || DEFAULT_ROLE_BADGE_STYLE,
  }
})

const userDisplayName = computed<string>(() => {
  const id = accountStore.userId || userStore.currentUser.email || ''
  const name = accountStore.userName || userStore.currentUser.name || ''
  if (id && name) {
    return `${id} (${name})`
  }
  return id || name || 'User'
})

// ─── methods ───────────────────────────────────────────────────────────────
function openProfileModal(): void {
  userStore.isShowUserMenu = false
  isShowProfileModal.value = true
}

function handleBeforeUnload(e: BeforeUnloadEvent): void {
  if (!downloadStore.hasActiveDownload) return
  e.preventDefault()
  e.returnValue = ''
}

function signOut(): void {
  userStore.logout()
  router.push('/login')
}

function navMouseEnter(e: MouseEvent, isActive: boolean): void {
  if (isActive) return
  const el = e.currentTarget as HTMLElement
  el.style.backgroundColor = 'rgba(255,255,255,0.07)'
  el.style.color = '#FFFFFF'
}

function navMouseLeave(e: MouseEvent, isActive: boolean): void {
  if (isActive) return
  const el = e.currentTarget as HTMLElement
  el.style.backgroundColor = 'transparent'
  el.style.color = '#E2E8F0'
}

function handleLogoClick(): void {
  router.push('/')
  window.dispatchEvent(new CustomEvent('reset-home-filter'))
}

// ─── watch ─────────────────────────────────────────────────────────────────
watch(
  () => userStore.isAuthenticated,
  (authed) => {
    if (authed) {
      userStore.fetchNotifications()
      userStore.connectNotificationSSE()
    } else {
      userStore.disconnectNotificationSSE()
    }
  }
)

watch(
  () => accountStore.accessToken,
  (token) => {
    if (token) {
      userStore.fetchNotifications()
      userStore.connectNotificationSSE()
    } else {
      userStore.disconnectNotificationSSE()
    }
  }
)

watch(
  () => userStore.roleAccentColor,
  (accent) => {
    if (!accent) return
    const root = document.documentElement
    root.style.setProperty('--accent', accent.color)
    root.style.setProperty('--accent-hover', accent.hover)
    root.style.setProperty('--accent-rgb', accent.rgb)
    root.style.setProperty('--info', accent.color)
  },
  { immediate: true }
)

// ─── lifecycle hooks ───────────────────────────────────────────────────────
onMounted(async () => {
  if (accountStore.accessToken) {
    await accountStore.fetchMenus()
    // 먼저 연결한다. fetchNotifications 는 연결이 없을 때만 연결하므로, 목록을 받은 뒤에 연결하면
    // 그 안에서 한 번, 여기서 기존 연결을 끊고 또 한 번 해 같은 스트림을 두 번 연다.
    userStore.connectNotificationSSE()
    await userStore.fetchNotifications()
  }
  window.addEventListener('beforeunload', handleBeforeUnload)
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})
</script>

<template>
  <div class="h-screen flex flex-col overflow-hidden" style="background-color: var(--background)">
    <!-- Top Navigation Bar (Hidden on Login Page) -->
    <header
      v-if="!isLoginPage"
      class="relative z-[1000] flex-shrink-0 border-b flex flex-col"
      style="
        position: relative;
        z-index: 1000;
        height: var(--header-height);
        background-color: var(--surface-dark);
        border-color: var(--border-dark);
      "
    >
      <div class="flex-1 px-6 flex items-center justify-between">
        <!-- Left: Logo + Navigation -->
        <div class="flex items-center gap-8">
          <a href="/" @click.prevent="handleLogoClick" class="flex items-center">
            <img
              :src="logoImage"
              alt="KRISS 한국표준과학연구원"
              style="width: 220px; height: auto; object-fit: contain"
            />
          </a>

          <!-- Navigation Menu -->
          <nav class="hidden lg:flex items-center gap-0.5">
            <RouterLink
              v-for="item in userStore.navItems"
              :key="item.id"
              :to="item.path"
              custom
              v-slot="{ isActive, navigate }"
            >
              <button
                @click="navigate"
                class="relative h-10 px-4 rounded-lg flex items-center gap-2 text-sm font-medium transition-all duration-200 cursor-pointer"
                :style="{
                  backgroundColor: isActive
                    ? `rgba(${userStore.roleAccentColor.rgb}, 0.15)`
                    : 'transparent',
                  color: isActive ? userStore.roleAccentColor.color : '#E2E8F0',
                }"
                @mouseenter="navMouseEnter($event, isActive)"
                @mouseleave="navMouseLeave($event, isActive)"
              >
                <span>{{ item.label }}</span>
                <!-- Active underline indicator -->
                <span
                  v-if="isActive"
                  class="absolute bottom-1 left-4 right-4 h-0.5 rounded-full transition-all duration-300"
                  :style="{ backgroundColor: userStore.roleAccentColor.color }"
                />
              </button>
            </RouterLink>
          </nav>
        </div>

        <div class="flex-1" />

        <!-- Right: Actions + User -->
        <div class="flex items-center gap-2">
          <!-- Actions only for logged in -->
          <template v-if="userStore.isAuthenticated">
            <!-- Download Manager -->
            <div class="relative">
              <button
                @click="downloadStore.isManagerOpen = !downloadStore.isManagerOpen"
                class="h-10 w-10 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                style="background-color: var(--navy-800)"
                v-text-tooltip="'다운로드 관리'"
              >
                <Download class="h-5 w-5" style="color: var(--gray-400)" />
                <span
                  v-if="activeDownloadCount > 0"
                  class="absolute top-1.5 right-1.5 h-5 w-5 rounded-full text-xs font-bold flex items-center justify-center text-white"
                  style="background-color: var(--accent)"
                  >{{ activeDownloadCount }}</span
                >
              </button>
            </div>

            <!-- Notification Bell -->
            <div class="relative">
              <button
                @click="userStore.isShowNotifications = !userStore.isShowNotifications"
                class="h-10 w-10 rounded-lg flex items-center justify-center transition-colors relative cursor-pointer"
                style="background-color: var(--navy-800)"
                v-text-tooltip="'알림 확인'"
              >
                <Bell class="h-5 w-5" style="color: var(--gray-400)" />
                <span
                  v-if="userStore.unreadNotificationCount > 0"
                  class="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10.5px] font-bold flex items-center justify-center text-white"
                  style="background-color: var(--error)"
                >
                  {{ userStore.unreadNotificationCount > 99 ? '99+' : userStore.unreadNotificationCount }}
                </span>
              </button>

              <!-- Notification Popover -->
              <NotificationDropdown v-if="userStore.isShowNotifications" />
            </div>

            <!-- User Menu -->
            <div class="relative">
              <button
                @click="userStore.isShowUserMenu = !userStore.isShowUserMenu"
                class="h-10 px-3 rounded-lg flex items-center gap-2 transition-colors cursor-pointer"
                style="background-color: var(--navy-800)"
              >
                <span class="text-sm font-medium" style="color: var(--foreground-on-dark)">
                  {{ userDisplayName }}
                </span>
                <ChevronDown class="h-4 w-4" style="color: var(--gray-400)" />
              </button>

              <!-- User Dropdown -->
              <template v-if="userStore.isShowUserMenu">
                <div class="fixed inset-0 z-40" @click="userStore.isShowUserMenu = false" />
                <div
                  class="absolute right-0 top-14 w-64 rounded-lg shadow-xl border z-50"
                  style="background-color: var(--surface); border-color: var(--border)"
                >
                  <div class="p-4 border-b" style="border-color: var(--border)">
                    <div class="flex items-center justify-between gap-2 flex-wrap">
                      <p class="font-semibold" style="color: var(--foreground)">
                        {{ userStore.currentUser.name }}
                      </p>
                      <!-- 권한 배지 -->
                      <span
                        v-if="userRoleBadge.label"
                        class="flex-shrink-0"
                        :style="{
                          backgroundColor: userRoleBadge.style.bg,
                          color: userRoleBadge.style.text,
                          border: `1px solid ${userRoleBadge.style.text}33`,
                          fontSize: '11px', fontWeight: 600,
                          padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap',
                        }"
                      >{{ userRoleBadge.label }}</span>
                    </div>
                    <p class="text-sm" style="color: var(--foreground-muted)">
                      {{ userStore.currentUser.email }}
                    </p>
                  </div>
                  <div class="py-2">
                    <button
                      @click="openProfileModal"
                      class="w-full px-4 py-2 text-left text-sm flex items-center gap-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                      style="color: var(--foreground)"
                    >
                      <User class="h-4 w-4" />
                      내 정보 수정
                    </button>
                  </div>
                  <div class="py-2 border-t" style="border-color: var(--border)">
                    <button
                      class="w-full px-4 py-2 text-left text-sm flex items-center gap-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                      style="color: var(--error)"
                      @click="signOut"
                    >
                      <LogOut class="h-4 w-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </template>

          <!-- Login button for unauthenticated users -->
          <template v-else>
            <button
              @click="router.push('/login')"
              class="h-10 px-5 rounded-lg text-sm font-bold flex items-center gap-2 transition-all active:scale-[0.98]"
              style="background-color: var(--accent); color: white"
            >
              <LogIn class="h-4 w-4" />
              로그인
            </button>
          </template>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <div ref="mainContentRef" class="flex-1 overflow-y-auto">
      <RouterView />
    </div>

    <!-- Global Dialogs & Modals -->
    <DialogAlert />
    <SimpleAlert />
    <SimpleLoading />
    <GlobalTooltip />
    <ProfileModal :is-open="isShowProfileModal" @close="isShowProfileModal = false" />
    <DownloadManagerModal :is-open="downloadStore.isManagerOpen" @close="downloadStore.isManagerOpen = false" />
  </div>
</template>
